import { TASK_STATUS } from 'modules/work-implementation/constants';
import { TASK_TYPE, TASK_TYPE_LABEL } from '../../constants';

// sample structure of taskConfig
// {
//   "columnFootingDrawings": 2,
//   "columnRange": {
//       "start": 1,
//       "end": 2
//   },
//   "slabDrawings": 7,
//   "slabsRange": {
//       "start": 8,
//       "end": 14
//   },
//   "totalDrawings": 7
// }

const COLUMN_DRAWING_PREFIX = 'C';
const SLAB_DRAWING_PREFIX = 'S';
const TASK_VARIANTS_COUNT = 2;

/**
 * @param {Object} params
 * @param {string} params.prefix
 * @param {{ start: number, end?: number }} params.range
 * @param {Map<string, string>} params.assignedTaskMap
 * @returns {Array<{ taskKey: string, name: string, status: string }>}
 */
function createTasksForRange({ prefix, range, assignedTaskMap }) {
  if (!range?.start) return [];

  const start = range.start;
  const end = range.end ?? start;

  const tasks = [];

  for (let n = start; n <= end; n++) {
    for (let i = 1; i <= TASK_VARIANTS_COUNT; i++) {
      const type = TASK_TYPE.DESIGN;
      const typeLabel = TASK_TYPE_LABEL.DESIGN;
      const name = `${prefix}${n}.${i} ${typeLabel}`;
      const taskKey = `${prefix}-${n}-${i}-${type}`;
      const groupKey = `${prefix}${n}`;
      const groupLabel = `${groupKey} ${typeLabel}`;

      tasks.push({
        taskKey,
        name,
        type,
        status: assignedTaskMap.get(name)?.status ?? TASK_STATUS.UNASSIGNED,
        id: assignedTaskMap.get(name)?.id ?? null,
        groupKey,
        groupLabel,
      });
    }

    for (let i = 1; i <= TASK_VARIANTS_COUNT; i++) {
      const type = TASK_TYPE.DRAWING;
      const typeLabel = TASK_TYPE_LABEL.DRAWING;
      const name = `${prefix}${n}.${i} ${typeLabel}`;
      const taskKey = `${prefix}-${n}-${i}-${type}`;
      const groupKey = `${prefix}${n}`;
      const groupLabel = `${groupKey} ${typeLabel}`;
      tasks.push({
        taskKey,
        name,
        type,
        status: assignedTaskMap.get(name)?.status ?? TASK_STATUS.UNASSIGNED,
        id: assignedTaskMap.get(name)?.id ?? null,
        groupKey,
        groupLabel,
      });
    }
  }

  return tasks;
}

/**
 * Generates a list of tasks for a given task configuration and assigned tasks.
 * @param {Object} taskConfig - The task configuration.
 * @param {Array} assignedTasks - The assigned tasks.
 * @returns {Array} The list of tasks.
 */
export function generateTaskList(taskConfig, assignedTasks) {
  const assignedTaskMap = new Map(
    (assignedTasks ?? []).map((task) => [task.name, { status: task.status, id: task.id }]),
  );

  const allTasks = [];

  allTasks.push(
    ...createTasksForRange({
      prefix: COLUMN_DRAWING_PREFIX,
      range: taskConfig.columnRange,
      assignedTaskMap,
    }),
  );

  allTasks.push(
    ...createTasksForRange({
      prefix: SLAB_DRAWING_PREFIX,
      range: taskConfig.slabsRange,
      assignedTaskMap,
    }),
  );

  return allTasks;
}

/**
 * Filters tasks by task type.
 * @param {Array} tasks - The list of tasks.
 * @param {string} taskType - The type of task.
 * @returns {Array} The list of filtered tasks.
 */
export function filterTasksByType(tasks, taskType) {
  return tasks.filter((task) => task.type === taskType);
}

/**
 * Groups tasks by group key.
 * @param {Array} tasks - The list of tasks.
 * @returns {Object} The grouped tasks.
 */
export function groupTasksByGroupKey(tasks) {
  return tasks.reduce((acc, task) => {
    acc[task.groupKey] ??= {
      label: task.groupLabel,
      tasks: [],
    };

    acc[task.groupKey].tasks.push(task);
    return acc;
  }, {});
}
